"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import Image from "next/image"
import type { DataImportCrmSource, DataImportStatistics, WizardStep } from "@/schema/data-import"
import type { DataImportSourceType } from "@/schema/data-import-source"
import { useQueryClient } from "@tanstack/react-query"
import { Button, Card, Input, Label, Switch } from "@ui/shad"
import { Stepper, StepperStep } from "@ui/stepper"
import { AlertCircle, ArrowLeft, ArrowRight, CheckCircle2, Loader2, Upload } from "lucide-react"
import { toast } from "sonner"

import { useFetchCurrentCompany } from "@/hooks/api/companies"
import {
  useCreateDataImportSource,
  useFanOutDataImportSource,
  useFetchDataImportSources,
  useSubmitTwoFactorCodeForSource,
  useUpdateDataImportSourceCredentials,
  useUploadDataImportSourceFile,
} from "@/hooks/api/data-import-sources"
import { useFetchDataImport, useUpdateDataImport } from "@/hooks/api/data-imports"
import { useFetchHubspotIntegration, useGetHubspotAuthUri } from "@/hooks/api/hubspot-integrations"
import { useRedtailCredentialCheck } from "@/hooks/api/redtail"
import { useFetchSalesforceIntegration, useGetSalesforceAuthUri } from "@/hooks/api/salesforce-integrations"
import { useBookId } from "@/hooks/use-book-id"
import { CrmExportInstructions } from "@/components/data-imports/crm-export-instructions"
import { ConfirmDialog } from "@/components/dialogs"
import { CustomDialog } from "@/components/dialogs/custom-dialog"

import { CredentialReEntry } from "./credential-re-entry"
import { DataMappingsStep, DataMappingsStepRef } from "./data-mappings-step"
import { ExtractionProgress } from "./extraction-progress"
import { FilterStep, FilterStepRef } from "./filter-step"
import { ImportCompleteSummary } from "./import-complete-summary"
import { ImportProgress } from "./import-progress"
import { MapTeamStep, MapTeamStepRef } from "./map-team-step"
import { ReviewStep, ReviewStepHandle, ReviewStepStatus } from "./review-step"
import { SourceExtractionProgress } from "./source-extraction-progress"
import { TwoFactorPrompt } from "./two-factor-prompt"

interface DataImportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  dataImportId?: string | null
  dataImportSourceId?: string | null
  bookId?: string | null
  pendingOauthImportSourceOnOpen?: "salesforce" | "hubspot" | null
  autoCloseOnImporting?: boolean
  onImportStarted?: () => void
}

type Step =
  | "select-source"
  | "upload-file"
  | "import-workflows"
  | "filter"
  | "data-mappings"
  | "map-team"
  | "review"
  | "importing"
  | "completed"
  | "enterprise-processing"

type UploadScope = "book" | "company"
type ConnectedImportSource = "salesforce" | "hubspot"

function isConnectedImportSource(sourceType: DataImportSourceType | null): sourceType is ConnectedImportSource {
  return sourceType === "salesforce" || sourceType === "hubspot"
}

// Map backend wizard_step to frontend Step
const wizardStepToStep = (wizardStep: WizardStep): Step => {
  switch (wizardStep) {
    case "filter":
      return "filter"
    case "data_mappings":
      return "data-mappings"
    case "review":
      return "review"
  }
}

// Map frontend Step to backend wizard_step
const stepToWizardStep = (step: Step): WizardStep | null => {
  switch (step) {
    case "filter":
      return "filter"
    case "data-mappings":
      return "data_mappings"
    case "review":
      return "review"
    default:
      return null
  }
}

const ALL_STEPS: { id: Step; title: string }[] = [
  { id: "select-source", title: "Select CRM source" },
  { id: "upload-file", title: "Upload export file" },
  { id: "map-team", title: "Map your team" },
  { id: "import-workflows", title: "Import workflows / opportunities" },
  { id: "filter", title: "Filter contacts" },
  { id: "data-mappings", title: "Map contact types" },
  { id: "review", title: "Review all data" },
]

type SourceTypeOption = {
  type: DataImportSourceType
  name: string
  logo: string
  fileType: string
  fileAccept: string
}

const SOURCE_TYPES: SourceTypeOption[] = [
  {
    type: "redtail",
    name: "Redtail",
    logo: "/redtail.png",
    fileType: "SQL database file",
    fileAccept: ".sql,.zip",
  },
  {
    type: "wealthbox",
    name: "Wealthbox",
    logo: "/wealthbox.svg",
    fileType: "JSON export file",
    fileAccept: ".json,.zip",
  },
  {
    type: "salesforce",
    name: "Salesforce",
    logo: "/salesforce.svg",
    fileType: "JSON import file",
    fileAccept: ".json",
  },
  {
    type: "hubspot",
    name: "HubSpot",
    logo: "/hubspot.png",
    fileType: "JSON import file",
    fileAccept: ".json",
  },
]

export function DataImportDialog({
  open,
  onOpenChange,
  dataImportId,
  dataImportSourceId,
  bookId: bookIdOverride,
  pendingOauthImportSourceOnOpen,
  autoCloseOnImporting,
  onImportStarted,
}: DataImportDialogProps) {
  const isEditing = !!dataImportId

  const [step, setStep] = useState<Step>("select-source")
  const [completionStats, setCompletionStats] = useState<DataImportStatistics | null>(null)
  const [uploadScope, setUploadScope] = useState<UploadScope | null>(null)
  const [selectedType, setSelectedType] = useState<DataImportSourceType | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [uploadedFileSignedId, setUploadedFileSignedId] = useState<string | null>(null)
  const [createdSourceId, setCreatedSourceId] = useState<string | null>(null)
  const [createdImportId, setCreatedImportId] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [filterStepPending, setFilterStepPending] = useState(false)
  const [dataMappingsStepPending, setDataMappingsStepPending] = useState(false)
  const [mapTeamStepPending, setMapTeamStepPending] = useState(false)
  const [mapTeamReady, setMapTeamReady] = useState(false)
  const [dataMappingsButtonLabel, setDataMappingsButtonLabel] = useState("Apply mappings")
  const [reviewStatus, setReviewStatus] = useState<ReviewStepStatus>({
    pendingCount: 0,
    includedCount: 0,
    isStartPending: false,
    isSavePending: false,
  })
  const [importWorkflows, setImportWorkflows] = useState(false)
  const [importOpportunities, setImportOpportunities] = useState(false)
  const [crmUsername, setCrmUsername] = useState("")
  const [crmPassword, setCrmPassword] = useState("")
  const [redtailCredentialError, setRedtailCredentialError] = useState<string | null>(null)
  // Reliable signal that a Redtail login was provided for this import (set at create time).
  const [redtailLoginEntered, setRedtailLoginEntered] = useState(false)
  const redtailCredentialCheck = useRedtailCredentialCheck()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const fileUploadGenerationRef = useRef(0)
  const filterStepRef = useRef<FilterStepRef>(null)
  const dataMappingsStepRef = useRef<DataMappingsStepRef>(null)
  const mapTeamStepRef = useRef<MapTeamStepRef>(null)
  const reviewStepRef = useRef<ReviewStepHandle>(null)

  const queryClient = useQueryClient()
  const { data: hubspotIntegration } = useFetchHubspotIntegration()
  const hubspotAuth = useGetHubspotAuthUri()
  const { data: salesforceIntegration } = useFetchSalesforceIntegration()
  const salesforceAuth = useGetSalesforceAuthUri()
  const { data: currentCompany } = useFetchCurrentCompany()
  const isEnterprise = currentCompany?.enterprise === true
  // Solo trial auto-classifies and imports on upload with no review step. Enterprise
  // (Slant-team-handled) takes precedence if a company somehow has both flags.
  const isSoloTrial = currentCompany?.solo_trial === true && !isEnterprise
  const routeBookId = useBookId()
  const bookId = bookIdOverride ?? routeBookId
  const createCompanySource = useCreateDataImportSource()
  const uploadSourceFile = useUploadDataImportSourceFile()
  const fanOutSource = useFanOutDataImportSource()
  const { mutate: fanOutCreatedSource, isPending: isFanOutPending } = fanOutSource
  const activeSourceId = createdSourceId ?? dataImportSourceId ?? null
  const { data: dataImportSources } = useFetchDataImportSources({ refetchInterval: activeSourceId ? 2000 : undefined })
  const submitSourceTwoFactor = useSubmitTwoFactorCodeForSource(activeSourceId ?? "")
  const updateSourceCredentials = useUpdateDataImportSourceCredentials(activeSourceId ?? "")
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null)
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false)
  const updateImport = useUpdateDataImport(createdImportId ?? "")
  // Fetch fresh data import when editing
  const { data: dataImport, isLoading: isLoadingImport } = useFetchDataImport(dataImportId ?? "")

  // Reset state when dialog opens/closes or dataImportId changes
  useEffect(() => {
    if (open) {
      setRedtailLoginEntered(false)
      if (dataImportId && dataImport) {
        setSelectedType(dataImport.source_type)
        setImportWorkflows(dataImport.import_workflows)
        setImportOpportunities(dataImport.import_opportunities)
        setFile(null)
        setUploadedFileSignedId(null)
        setCreatedImportId(dataImport.id)

        const isFromSource = !!dataImport.data_import_source_id

        if (isEnterprise && dataImport.status !== "completed") {
          // Enterprise customers only enter the wizard to finish mapping their team;
          // otherwise reopening shows the "The Slant team is preparing your import" screen.
          const needsTeamMapping =
            dataImport.source_type === "redtail" && dataImport.has_crm_credentials && !dataImport.has_user_mapping
          setStep(needsTeamMapping ? "map-team" : "enterprise-processing")
          setIsProcessing(false)
        } else if (
          !isFromSource &&
          (dataImport.status === "extracting" ||
            dataImport.status === "pending" ||
            dataImport.status === "scraping" ||
            dataImport.status === "awaiting_2fa" ||
            dataImport.status === "failed")
        ) {
          // If still extracting, scraping, awaiting 2FA, or failed, show the extraction progress screen.
          // Fan-out (source-backed) imports never land here — they're pre-populated.
          const reopenStep: Step =
            dataImport.source_type === "wealthbox" && (dataImport.import_workflows || dataImport.import_opportunities)
              ? "import-workflows"
              : "upload-file"
          setStep(reopenStep)
          setIsProcessing(true)
        } else if (dataImport.status === "importing") {
          // If importing, show the import progress screen
          setStep("importing")
          setIsProcessing(false)
        } else if (dataImport.status === "completed") {
          // If completed, show the completion screen
          setStep("completed")
          setIsProcessing(false)
        } else {
          // Use wizard_step from fresh backend data to restore the correct step
          setStep(wizardStepToStep(dataImport.wizard_step))
          setIsProcessing(false)
        }
      } else if (dataImportSourceId) {
        setSelectedType("wealthbox")
        setImportWorkflows(true)
        setImportOpportunities(false)
        setCreatedSourceId(null)
        setCreatedImportId(null)
        setStep("import-workflows")
        setIsProcessing(true)
        setIsUploading(false)
      } else if (!dataImportId) {
        setStep("select-source")
        setUploadScope(null)
        setSelectedType(null)
        setFile(null)
        setUploadedFileSignedId(null)
        setCreatedSourceId(null)
        setCreatedImportId(null)
        setCompletionStats(null)
        setIsProcessing(false)
        setIsUploading(false)
        setImportWorkflows(false)
        setImportOpportunities(false)
        setCrmUsername("")
        setCrmPassword("")
        setRedtailCredentialError(null)

        if (pendingOauthImportSourceOnOpen) {
          setSelectedType(pendingOauthImportSourceOnOpen)
          setStep("enterprise-processing")
        }
      }
    }
  }, [open, dataImportId, dataImport, dataImportSourceId, isEnterprise, pendingOauthImportSourceOnOpen])

  useEffect(() => {
    if (!open || !dataImportSourceId) return

    const source = dataImportSources?.find((item) => item.id === dataImportSourceId)
    if (!source) return

    setSelectedType(source.source_type)
    setImportWorkflows(source.import_workflows)
    setImportOpportunities(source.import_opportunities)
  }, [dataImportSourceId, dataImportSources, open])

  useEffect(() => {
    if (!open || !activeSourceId || !isProcessing) return

    const source = dataImportSources?.find((item) => item.id === activeSourceId)
    if (!source) return

    // Redtail imports with a login start on the Map-your-team step (not a backend wizard_step).
    const routeToMapTeam = selectedType === "redtail" && (redtailLoginEntered || Boolean(source.has_crm_credentials))

    // Enterprise imports only enter the wizard for Map-your-team; otherwise the Slant team drives.
    if (isEnterprise && !routeToMapTeam) return

    if (source.status === "failed") {
      setCreatedSourceId(null)
      setIsProcessing(false)
      toast.error(source.error_message || "Data source parsing failed")
      return
    }

    const bookImport = source.data_imports.find((item) => item.book_id === bookId)
    if (!bookImport) {
      // Solo trial fan-out + auto-start happen server-side (the FanOutOnReady subscriber)
      // once the source is ready. Wait for the staged import to appear rather than
      // triggering our own fan-out, which would not auto-start the import.
      if (isSoloTrial) return
      if (source.status !== "ready" || isFanOutPending) return

      fanOutCreatedSource(
        { sourceId: source.id },
        {
          onSuccess: (result) => {
            const createdImport = result.data_imports.find((item) => item.book_id === bookId) ?? result.data_imports[0]
            if (!createdImport) return

            setCreatedSourceId(null)
            setCreatedImportId(createdImport.id)
            setIsProcessing(false)
            toast.success("Import ready for review")
            setStep(routeToMapTeam ? "map-team" : wizardStepToStep(createdImport.wizard_step as WizardStep))
          },
          onError: () => {
            setCreatedSourceId(null)
            setIsProcessing(false)
          },
        }
      )
      return
    }

    setCreatedSourceId(null)
    setCreatedImportId(bookImport.id)
    setIsProcessing(false)

    if (isSoloTrial) {
      // No review step — the backend already started the import. Jump straight to progress.
      toast.success("Import started")
      setStep("importing")
      return
    }

    toast.success("Import ready for review")
    setStep(routeToMapTeam ? "map-team" : wizardStepToStep(bookImport.wizard_step as WizardStep))
  }, [
    activeSourceId,
    bookId,
    dataImportSources,
    fanOutCreatedSource,
    isEnterprise,
    isFanOutPending,
    isProcessing,
    isSoloTrial,
    open,
    redtailLoginEntered,
    selectedType,
  ])

  const pendingSource = useMemo(
    () => (activeSourceId ? dataImportSources?.find((item) => item.id === activeSourceId) : undefined),
    [activeSourceId, dataImportSources]
  )
  const isAwaitingTwoFactor = pendingSource?.status === "awaiting_2fa" && pendingSource.current_challenge_id != null
  const isVerifyingTwoFactor = pendingSource?.status === "awaiting_2fa" && pendingSource.current_challenge_id == null
  const isAwaitingCredentials = pendingSource?.status === "awaiting_credentials"
  const isWorkflowVerificationWindow =
    isProcessing &&
    pendingSource?.source_type === "wealthbox" &&
    (pendingSource.import_workflows || pendingSource.import_opportunities) &&
    pendingSource.workflow_scrape_authenticated_at == null &&
    pendingSource.workflow_scrape_completed_at == null &&
    pendingSource.status !== "awaiting_2fa" &&
    pendingSource.status !== "awaiting_credentials" &&
    pendingSource.status !== "failed"

  const handleSourceTwoFactor = (code: string) => {
    setTwoFactorError(null)
    submitSourceTwoFactor.mutate(code, {
      onError: (error) => {
        setTwoFactorError(error.message || "Verification failed. Please try again.")
      },
    })
  }

  const handleCredentialRetry = () => {
    if (credentialsMissing) return

    updateSourceCredentials.mutate({
      crmUsername: crmUsername.trim(),
      crmPassword,
    })
  }

  const selectedSource = SOURCE_TYPES.find((s) => s.type === selectedType)
  const wealthboxAuthenticatedImportSelected = selectedType === "wealthbox" && (importWorkflows || importOpportunities)

  // Build visible steps:
  // - Enterprise flows always end at upload-file — the Slant team handles the rest
  // - OAuth-backed JSON imports skip workflow auth, filter, and data-mappings steps
  // "Map your team" only applies to Redtail imports that have a saved login.
  const redtailHasCredentials =
    selectedType === "redtail" &&
    (redtailLoginEntered ||
      Boolean(pendingSource?.has_crm_credentials) ||
      Boolean(dataImport?.has_crm_credentials) ||
      (crmUsername.trim() !== "" && crmPassword.trim() !== ""))

  const visibleSteps = useMemo(() => {
    const base = (() => {
      if (isSoloTrial && !isEditing) {
        // Solo trial auto-classifies and imports on upload: pick a source, upload, done.
        return ALL_STEPS.filter((s) => s.id === "select-source" || s.id === "upload-file")
      }
      if (isEnterprise && !isEditing) {
        // Enterprise customers upload (+ map their team for Redtail); the Slant team handles the rest.
        return ALL_STEPS.filter((s) => s.id === "select-source" || s.id === "upload-file" || s.id === "map-team")
      }
      if (isConnectedImportSource(selectedType)) {
        return ALL_STEPS.filter((s) => s.id !== "filter" && s.id !== "data-mappings" && s.id !== "import-workflows")
      }
      if (selectedType !== "wealthbox") {
        return ALL_STEPS.filter((s) => s.id !== "import-workflows")
      }
      return ALL_STEPS
    })()
    return base.filter((s) => s.id !== "map-team" || redtailHasCredentials)
  }, [isEditing, isEnterprise, isSoloTrial, selectedType, redtailHasCredentials])

  // Build steps with locked state
  // All steps up to and including the last pre-wizard step lock once we reach filter/review
  const steps: StepperStep[] = useMemo(() => {
    const currentIndex = visibleSteps.findIndex((s) => s.id === step)
    const workflowsIndex = visibleSteps.findIndex((s) => s.id === "import-workflows")
    const uploadFileIndex = visibleSteps.findIndex((s) => s.id === "upload-file")
    const lastPreWizardIndex = workflowsIndex !== -1 ? workflowsIndex : uploadFileIndex

    return visibleSteps.map((s, index) => {
      if (lastPreWizardIndex !== -1 && index <= lastPreWizardIndex) {
        return { ...s, locked: isEditing || currentIndex > lastPreWizardIndex }
      }
      return { ...s, locked: false }
    })
  }, [isEditing, step, visibleSteps])

  // For non-wizard steps (importing, completed, enterprise-processing), show the last step index
  const currentStepIndex =
    step === "importing" || step === "completed" || step === "enterprise-processing"
      ? visibleSteps.length - 1
      : steps.findIndex((s) => s.id === step)

  const handleTypeSelect = async (type: DataImportSourceType) => {
    if (type === "salesforce") {
      if (salesforceIntegration?.connected) {
        setSelectedType(type)
        setStep("enterprise-processing")
        return
      }

      const { auth_uri } = await salesforceAuth.mutateAsync()
      window.location.href = auth_uri
      return
    }

    if (type === "hubspot") {
      if (hubspotIntegration?.connected) {
        setSelectedType(type)
        setStep("enterprise-processing")
        return
      }

      const { auth_uri } = await hubspotAuth.mutateAsync()
      window.location.href = auth_uri
      return
    }

    setSelectedType(type)
    if (type !== "wealthbox" && type !== "redtail") setImportOpportunities(false)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) return

    const acceptedExtensions = selectedSource?.fileAccept.split(",") ?? []
    const fileName = selectedFile.name.toLowerCase()
    if (acceptedExtensions.length > 0 && !acceptedExtensions.some((ext) => fileName.endsWith(ext))) {
      toast.error(`Please select a ${acceptedExtensions.join(" or ")} file`)
      return
    }

    setFile(selectedFile)
    setUploadedFileSignedId(null)

    if (selectedType === "wealthbox") {
      const generation = fileUploadGenerationRef.current + 1
      fileUploadGenerationRef.current = generation
      setIsUploading(true)
      uploadSourceFile.mutate(selectedFile, {
        onSuccess: (signedId) => {
          if (fileUploadGenerationRef.current !== generation) return
          setUploadedFileSignedId(signedId)
          setIsUploading(false)
        },
        onError: () => {
          if (fileUploadGenerationRef.current !== generation) return
          setIsUploading(false)
        },
      })
    }
  }

  const handleNext = () => {
    const currentIndex = visibleSteps.findIndex((s) => s.id === step)
    if (currentIndex < visibleSteps.length - 1) {
      // Validate current step before proceeding
      if (step === "select-source" && !selectedType) {
        toast.error("Please select a source")
        return
      }
      if (step === "upload-file" && !file) {
        toast.error("Please upload a file")
        return
      }
      if (step === "upload-file" && !isConnectedImportSource(selectedType) && !uploadScope) {
        toast.error("Please choose where to import this data")
        return
      }
      const newStep = visibleSteps[currentIndex + 1].id
      setStep(newStep)

      // Persist wizard step to backend for steps that are part of the wizard (filter, data-mappings, review)
      const wizardStep = stepToWizardStep(newStep)
      if (wizardStep && createdImportId) {
        updateImport.mutate({ wizard_step: wizardStep })
      }
    }
  }

  const handleBack = () => {
    const currentIndex = visibleSteps.findIndex((s) => s.id === step)
    // Don't go back past locked pre-wizard steps when editing — editing always
    // re-enters at filter/data-mappings/review
    const filterIndex = visibleSteps.findIndex((s) => s.id === "filter")
    const reviewIndex = visibleSteps.findIndex((s) => s.id === "review")
    const firstWizardIndex = filterIndex !== -1 ? filterIndex : reviewIndex
    const minIndex = isEditing ? firstWizardIndex : 0
    if (currentIndex > minIndex) {
      const newStep = visibleSteps[currentIndex - 1].id
      setStep(newStep)

      // Persist wizard step to backend for steps that are part of the wizard (filter, data-mappings, review)
      const wizardStep = stepToWizardStep(newStep)
      if (wizardStep && createdImportId) {
        updateImport.mutate({ wizard_step: wizardStep })
      }
    }
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true)
      return
    }

    if (isWorkflowVerificationWindow) {
      setShowLeaveConfirm(true)
      return
    }

    onOpenChange(false)
  }

  const handleClose = () => handleOpenChange(false)

  const handleConfirmLeave = () => {
    setShowLeaveConfirm(false)
    onOpenChange(false)
  }

  const handleCreate = async () => {
    if (!selectedType || !file) return
    const credentialsEntered = crmUsername.trim() !== "" && crmPassword.trim() !== ""
    // Hidden Wealthbox credentials must never trigger authentication on their own.
    const sendCredentials =
      selectedType === "wealthbox" ? wealthboxAuthenticatedImportSelected && credentialsEntered : credentialsEntered
    if (selectedType === "wealthbox" && (importWorkflows || importOpportunities) && credentialsMissing) {
      toast.error("Please enter your Wealthbox credentials")
      return
    }
    if (selectedType === "redtail" && (importWorkflows || importOpportunities) && credentialsMissing) {
      toast.error("Please enter your Redtail credentials")
      return
    }
    setRedtailLoginEntered(sendCredentials)

    // OAuth-backed JSON files are always book-specific, so the scope picker is
    // hidden and the upload is forced into the current book.
    const isOauthSource = isConnectedImportSource(selectedType)

    if (!isOauthSource && !isSoloTrial && !uploadScope) {
      toast.error("Please choose where to import this data")
      return
    }
    // Solo trial always imports into the current book: never company-wide.
    const isCompanyScope = !isOauthSource && !isSoloTrial && uploadScope === "company"
    if (!isCompanyScope && !bookId) {
      toast.error("No book selected for this import")
      return
    }

    setIsUploading(true)
    try {
      if (selectedType === "redtail" && sendCredentials) {
        setRedtailCredentialError(null)
        try {
          await redtailCredentialCheck.mutateAsync({
            crmUsername: crmUsername.trim(),
            crmPassword: crmPassword,
          })
        } catch (error) {
          const message = error instanceof Error ? error.message : "We couldn't verify those credentials."
          setRedtailCredentialError(message)
          setIsUploading(false)
          return
        }
      }

      const sourceBookId = isCompanyScope ? null : (bookId ?? null)

      if (isEnterprise) {
        // Enterprise uploads always go through the DataImportSource pipeline so the source
        // shows up in the list and creates the per-book DataImports. The scope the
        // user picked on the upload-file page determines book_id: "book" → current book,
        // "company" → null (stage the import for every book in the company).
        const source = await createCompanySource.mutateAsync({
          sourceType: selectedType,
          file,
          bookId: sourceBookId,
          importWorkflows,
          importOpportunities: selectedType === "wealthbox" || selectedType === "redtail" ? importOpportunities : false,
          importFileSignedId: selectedType === "wealthbox" ? (uploadedFileSignedId ?? undefined) : undefined,
          crmUsername: sendCredentials ? crmUsername : undefined,
          crmPassword: sendCredentials ? crmPassword : undefined,
        })
        setIsUploading(false)
        if (selectedType === "redtail" && sendCredentials) {
          // Straight to Map-your-team — the roster comes from the Redtail API, so it
          // doesn't need extraction. The Slant team takes over after the mapping.
          setCreatedSourceId(source.id)
          setStep("map-team")
          toast.success("Data source uploaded")
        } else {
          setStep("enterprise-processing")
        }
      } else {
        const source = await createCompanySource.mutateAsync({
          sourceType: selectedType,
          file,
          bookId: sourceBookId,
          importWorkflows,
          importOpportunities: selectedType === "wealthbox" || selectedType === "redtail" ? importOpportunities : false,
          importFileSignedId: selectedType === "wealthbox" ? (uploadedFileSignedId ?? undefined) : undefined,
          crmUsername: sendCredentials ? crmUsername : undefined,
          crmPassword: sendCredentials ? crmPassword : undefined,
        })
        setIsUploading(false)
        setCreatedSourceId(source.id)
        setIsProcessing(true)
        toast.success(
          wealthboxAuthenticatedImportSelected
            ? "Data source uploaded; verifying Wealthbox login"
            : "Data source uploaded; parsing started"
        )
      }
    } catch {
      setIsUploading(false)
    }
  }

  const handleExtractionComplete = () => {
    setIsProcessing(false)
    toast.success("Import ready for review")
    if (isConnectedImportSource(selectedType)) {
      setStep("review")
    } else {
      // Map-your-team is the first configuration step for Redtail imports with a login.
      setStep(redtailHasCredentials ? "map-team" : "filter")
    }
  }

  const handleImportComplete = (statistics: DataImportStatistics) => {
    toast.success("Import completed successfully")
    queryClient.invalidateQueries({ queryKey: ["data-imports"] })
    setCompletionStats(statistics)
    setStep("completed")
  }

  const editingFilterIndex = visibleSteps.findIndex((s) => s.id === "filter")
  const editingReviewIndex = visibleSteps.findIndex((s) => s.id === "review")
  const editingFirstWizardIndex = editingFilterIndex !== -1 ? editingFilterIndex : editingReviewIndex
  const canGoBack = isEditing ? currentStepIndex > editingFirstWizardIndex : currentStepIndex > 0
  const isLastStep = step === "review"
  const isImportingStep = step === "importing"
  const isCompletedStep = step === "completed"
  const isEnterpriseProcessingStep = step === "enterprise-processing"
  const isCreateStep =
    !isEditing &&
    !isProcessing &&
    (selectedType === "wealthbox" && !isEnterprise && !isSoloTrial
      ? step === "import-workflows"
      : step === "upload-file")
  // Solo trial always imports into the current book, so the scope picker is hidden.
  const scopeMissing = !isConnectedImportSource(selectedType) && !isSoloTrial && !uploadScope
  // Wealthbox uses one authenticated session for either optional scrape.
  const credentialsMissing =
    (selectedType === "wealthbox" || selectedType === "redtail") &&
    (importWorkflows || importOpportunities) &&
    (crmUsername.trim() === "" || crmPassword.trim() === "")
  return (
    <>
      <CustomDialog
        open={open}
        onOpenChange={handleOpenChange}
        title="Import data"
        customLayout="fullscreen"
        showCloseButton
        contentProps={{ onInteractOutside: (event) => event.preventDefault() }}
      >
        <div className="flex flex-1 overflow-hidden">
          {/* Left sidebar with stepper */}
          <div className="border-border w-64 shrink-0 border-r p-6">
            <h2 className="mb-6 text-lg font-semibold">
              {isEditing ? `${selectedSource?.name} import` : "New import"}
            </h2>
            <Stepper steps={steps} currentStepIndex={currentStepIndex} />
          </div>

          {/* Main content */}
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className={step === "review" ? "flex-1 overflow-hidden" : "flex-1 overflow-y-auto p-8"}>
              {isEditing && (isLoadingImport || !dataImport) ? (
                <div className="flex h-full items-center justify-center">
                  <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
                </div>
              ) : (
                <>
                  {step === "select-source" && (
                    <div className="mx-auto max-w-xl">
                      <h3 className="mb-2 text-lg font-medium">Select source</h3>
                      <p className="text-muted-foreground mb-6">Choose the CRM you want to import data from</p>

                      <div className="grid grid-cols-2 gap-4">
                        {SOURCE_TYPES.filter(
                          (source) => !isSoloTrial || source.type === "redtail" || source.type === "wealthbox"
                        ).map((source) => {
                          const isConnectingToSource =
                            (source.type === "salesforce" && salesforceAuth.isPending) ||
                            (source.type === "hubspot" && hubspotAuth.isPending)

                          return (
                            <Card
                              key={source.type}
                              className={`flex cursor-pointer flex-col items-center gap-3 p-6 transition-colors ${
                                selectedType === source.type
                                  ? "border-primary ring-primary/20 ring-2"
                                  : "hover:border-primary"
                              } ${isConnectingToSource ? "pointer-events-none opacity-70" : ""}`}
                              aria-disabled={isConnectingToSource}
                              onClick={() => handleTypeSelect(source.type)}
                            >
                              <Image
                                src={source.logo}
                                alt={`${source.name} logo`}
                                width={48}
                                height={48}
                                className="rounded object-contain"
                              />
                              <span className="font-medium">
                                {isConnectingToSource ? "Connecting..." : source.name}
                              </span>
                            </Card>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {step === "upload-file" && selectedSource && (
                    <div className="mx-auto max-w-xl">
                      {isProcessing && isAwaitingCredentials && pendingSource ? (
                        <CredentialReEntry
                          sourceId={pendingSource.id}
                          sourceName={selectedSource.name}
                          errorMessage={pendingSource.error_message}
                        />
                      ) : isProcessing && (isVerifyingTwoFactor || submitSourceTwoFactor.isPending) ? (
                        <div className="mx-auto flex max-w-xl flex-col items-center gap-6 py-12">
                          <Loader2 className="text-muted-foreground h-16 w-16 animate-spin" />
                          <div className="text-center">
                            <h3 className="text-lg font-medium">Verifying verification code</h3>
                            <p className="text-muted-foreground mt-1">
                              Confirming the code with {selectedSource.name}.
                            </p>
                          </div>
                        </div>
                      ) : isProcessing && isAwaitingTwoFactor ? (
                        <TwoFactorPrompt
                          sourceType={selectedType as DataImportCrmSource}
                          isPending={false}
                          error={twoFactorError}
                          onSubmit={handleSourceTwoFactor}
                        />
                      ) : isProcessing && createdImportId ? (
                        <ExtractionProgress
                          dataImportId={createdImportId}
                          sourceType={selectedType as DataImportCrmSource}
                          onComplete={handleExtractionComplete}
                        />
                      ) : isProcessing && !isAwaitingCredentials && pendingSource ? (
                        <SourceExtractionProgress source={pendingSource} sourceName={selectedSource.name} />
                      ) : isProcessing && !isAwaitingCredentials ? (
                        <div className="mx-auto flex max-w-xl flex-col items-center gap-6 py-12">
                          <Loader2 className="text-muted-foreground h-16 w-16 animate-spin" />

                          <div className="text-center">
                            <h3 className="text-lg font-medium">Processing your data</h3>
                            <p className="text-muted-foreground mt-1">
                              Extracting records from your uploaded {selectedSource.name} file.
                            </p>
                          </div>

                          <p className="text-muted-foreground mt-4 text-xs">
                            You can close this dialog. The import will continue in the background.
                          </p>
                        </div>
                      ) : step === "upload-file" ? (
                        <>
                          <h3 className="mb-2 text-lg font-medium">Upload {selectedSource.name} file</h3>
                          <p className="text-muted-foreground mb-6">
                            {isEnterprise
                              ? `Upload your ${selectedSource.fileType}. A Slant team member will import the data.`
                              : `Upload your ${selectedSource.fileType}`}
                          </p>

                          {!isConnectedImportSource(selectedType) && !isSoloTrial && (
                            <div className="mb-6">
                              <Label className="text-sm font-medium">Where would you like to import?</Label>
                              <div className="mt-2 grid grid-cols-2 gap-3">
                                <Card
                                  className={`flex cursor-pointer flex-col items-start gap-1 p-4 transition-colors ${
                                    uploadScope === "book"
                                      ? "border-primary ring-primary/20 ring-2"
                                      : "hover:border-primary"
                                  }`}
                                  onClick={() => setUploadScope("book")}
                                >
                                  <span className="text-sm font-medium">Just this book</span>
                                  <span className="text-muted-foreground text-xs">
                                    Import into the current book only.
                                  </span>
                                </Card>
                                <Card
                                  className={`flex cursor-pointer flex-col items-start gap-1 p-4 transition-colors ${
                                    uploadScope === "company"
                                      ? "border-primary ring-primary/20 ring-2"
                                      : "hover:border-primary"
                                  }`}
                                  onClick={() => setUploadScope("company")}
                                >
                                  <span className="text-sm font-medium">Entire company</span>
                                  <span className="text-muted-foreground text-xs">
                                    Stage the import for every book in the company.
                                  </span>
                                </Card>
                              </div>
                            </div>
                          )}

                          <input
                            ref={fileInputRef}
                            type="file"
                            accept={selectedSource.fileAccept}
                            onChange={handleFileChange}
                            className="hidden"
                          />
                          <div
                            className={`flex cursor-pointer flex-col items-center gap-4 rounded-lg border-2 border-dashed p-12 transition-colors ${
                              file ? "border-primary bg-primary/5" : "hover:border-primary"
                            }`}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <Upload className="text-muted-foreground h-12 w-12" />
                            {file ? (
                              <div className="text-center">
                                <p className="font-medium">{file.name}</p>
                                <p className="text-muted-foreground text-sm">
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              </div>
                            ) : (
                              <div className="text-center">
                                <p className="font-medium">Click to upload</p>
                                <p className="text-muted-foreground text-sm">Select your {selectedSource.fileType}</p>
                              </div>
                            )}
                          </div>

                          {selectedSource.type === "wealthbox" && (
                            <CrmExportInstructions sourceType="wealthbox" className="mt-6" />
                          )}

                          {selectedSource.type === "redtail" && !isSoloTrial && (
                            <div className="border-border mt-6 space-y-4 rounded-lg border p-4">
                              <div className="space-y-3">
                                <div className="space-y-0.5">
                                  <Label>Redtail login (optional)</Label>
                                  <p className="text-muted-foreground text-sm">
                                    Used to match your Redtail users to your Slant team and import workflows or
                                    opportunities. If you skip it, imported tasks are assigned to you (the book
                                    principal) instead of your teammates.
                                  </p>
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="redtail-username">Redtail username</Label>
                                  <Input
                                    id="redtail-username"
                                    type="text"
                                    placeholder="Enter your Redtail username"
                                    value={crmUsername}
                                    onChange={(e) => {
                                      setCrmUsername(e.target.value)
                                      setRedtailCredentialError(null)
                                    }}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="redtail-password">Redtail password</Label>
                                  <Input
                                    id="redtail-password"
                                    type="password"
                                    placeholder="Enter your Redtail password"
                                    value={crmPassword}
                                    onChange={(e) => {
                                      setCrmPassword(e.target.value)
                                      setRedtailCredentialError(null)
                                    }}
                                  />
                                  {redtailCredentialError && (
                                    <p className="text-red-accent text-xs">{redtailCredentialError}</p>
                                  )}
                                </div>
                                <p className="text-muted-foreground text-xs">
                                  Your login is stored encrypted and only used during import.
                                </p>
                              </div>

                              <div className="flex items-center justify-between border-t pt-4">
                                <div className="space-y-0.5">
                                  <Label htmlFor="import-workflows">Import workflows as projects</Label>
                                  <p className="text-muted-foreground text-sm">
                                    Create project templates from your Redtail workflows. Requires your Redtail login.
                                  </p>
                                </div>
                                <Switch
                                  id="import-workflows"
                                  checked={importWorkflows}
                                  onCheckedChange={setImportWorkflows}
                                />
                              </div>

                              <div className="flex items-center justify-between border-t pt-4">
                                <div className="space-y-0.5">
                                  <Label htmlFor="redtail-import-opportunities">Import opportunities</Label>
                                  <p className="text-muted-foreground text-sm">
                                    Create a pipeline, stages, and opportunities from your current Redtail data.
                                    Requires your Redtail login.
                                  </p>
                                </div>
                                <Switch
                                  id="redtail-import-opportunities"
                                  checked={importOpportunities}
                                  onCheckedChange={setImportOpportunities}
                                />
                              </div>
                            </div>
                          )}

                          {selectedSource.type === "redtail" && (
                            <CrmExportInstructions sourceType="redtail" className="mt-6" />
                          )}
                        </>
                      ) : null}
                    </div>
                  )}

                  {step === "import-workflows" && selectedSource && (
                    <div className="mx-auto max-w-xl">
                      {isProcessing &&
                      !isAwaitingCredentials &&
                      (isVerifyingTwoFactor || submitSourceTwoFactor.isPending) ? (
                        <div className="mx-auto flex max-w-xl flex-col items-center gap-6 py-12">
                          <Loader2 className="text-muted-foreground h-16 w-16 animate-spin" />
                          <div className="text-center">
                            <h3 className="text-lg font-medium">Verifying verification code</h3>
                            <p className="text-muted-foreground mt-1">
                              Confirming the code with {selectedSource.name}.
                            </p>
                          </div>
                        </div>
                      ) : isProcessing && isAwaitingTwoFactor ? (
                        <TwoFactorPrompt
                          sourceType={selectedType as DataImportCrmSource}
                          isPending={false}
                          error={twoFactorError}
                          onSubmit={handleSourceTwoFactor}
                        />
                      ) : isProcessing && isWorkflowVerificationWindow ? (
                        <div className="mx-auto flex max-w-xl flex-col items-center gap-6 py-12">
                          <Loader2 className="text-muted-foreground h-16 w-16 animate-spin" />

                          <div className="text-center">
                            <h3 className="text-lg font-medium">Logging in to {selectedSource.name}</h3>
                            <p className="text-muted-foreground mt-1">
                              Checking whether your account requires a 2FA code.
                            </p>
                          </div>
                        </div>
                      ) : isProcessing && !isAwaitingCredentials && pendingSource ? (
                        <SourceExtractionProgress source={pendingSource} sourceName={selectedSource.name} />
                      ) : isProcessing && !isAwaitingCredentials ? (
                        <div className="mx-auto flex max-w-xl flex-col items-center gap-6 py-12">
                          <Loader2 className="text-muted-foreground h-16 w-16 animate-spin" />

                          <div className="text-center">
                            <h3 className="text-lg font-medium">Processing your data</h3>
                            <p className="text-muted-foreground mt-1">
                              Extracting records from your uploaded {selectedSource.name} file.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <h3 className="mb-2 text-lg font-medium">Import workflows and opportunities</h3>
                          <p className="text-muted-foreground mb-6">
                            Choose any additional data you want Slant to retrieve from {selectedSource.name}. Both
                            options use the same secure login and verification flow.
                          </p>

                          <div className="border-border space-y-4 rounded-lg border p-4">
                            {isAwaitingCredentials && pendingSource && (
                              <div className="bg-yellow-background border-yellow-border text-yellow-foreground flex items-start gap-3 rounded-lg border p-4">
                                <AlertCircle className="mt-0.5 size-5 shrink-0" />
                                <div>
                                  <h4 className="text-sm font-medium">Login didn&apos;t work</h4>
                                  <p className="mt-1 text-sm">
                                    {pendingSource.error_message ||
                                      "Double-check your Wealthbox username and password and try again."}
                                  </p>
                                </div>
                              </div>
                            )}

                            <div className="space-y-4">
                              <div className="flex items-center justify-between gap-4">
                                <div className="space-y-0.5">
                                  <Label htmlFor="wealthbox-import-workflows">Import workflows as projects</Label>
                                  <p className="text-muted-foreground text-sm">
                                    Create Slant project templates from your workflows.
                                  </p>
                                </div>
                                <Switch
                                  id="wealthbox-import-workflows"
                                  checked={importWorkflows}
                                  onCheckedChange={setImportWorkflows}
                                  disabled={isAwaitingCredentials}
                                />
                              </div>

                              <div className="flex items-center justify-between gap-4 border-t pt-4">
                                <div className="space-y-0.5">
                                  <Label htmlFor="wealthbox-import-opportunities">Import opportunities</Label>
                                  <p className="text-muted-foreground text-sm">
                                    Create pipelines, stages, and opportunities from your current Wealthbox data.
                                  </p>
                                </div>
                                <Switch
                                  id="wealthbox-import-opportunities"
                                  checked={importOpportunities}
                                  onCheckedChange={setImportOpportunities}
                                  disabled={isAwaitingCredentials}
                                />
                              </div>
                            </div>

                            {wealthboxAuthenticatedImportSelected && (
                              <div className="space-y-3 border-t pt-4">
                                <div className="space-y-0.5">
                                  <Label>{selectedSource.name} login</Label>
                                  <p className="text-muted-foreground text-sm">
                                    We&apos;ll verify this login before continuing your import.
                                  </p>
                                </div>
                                <div className="grid gap-3 sm:grid-cols-2">
                                  <div className="space-y-2">
                                    <Label htmlFor="wealthbox-username">Username</Label>
                                    <Input
                                      id="wealthbox-username"
                                      type="text"
                                      placeholder={`Enter your ${selectedSource.name} username`}
                                      value={crmUsername}
                                      onChange={(e) => setCrmUsername(e.target.value)}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="wealthbox-password">Password</Label>
                                    <Input
                                      id="wealthbox-password"
                                      type="password"
                                      placeholder={`Enter your ${selectedSource.name} password`}
                                      value={crmPassword}
                                      onChange={(e) => setCrmPassword(e.target.value)}
                                    />
                                  </div>
                                </div>
                                <p className="text-muted-foreground text-xs">
                                  Your login is stored encrypted and only used during this import.
                                </p>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {step === "filter" && createdImportId && (
                    <FilterStep ref={filterStepRef} dataImportId={createdImportId} onNext={handleNext} />
                  )}

                  {step === "data-mappings" && createdImportId && (
                    <DataMappingsStep
                      ref={dataMappingsStepRef}
                      dataImportId={createdImportId}
                      onNext={handleNext}
                      onButtonLabelChange={setDataMappingsButtonLabel}
                    />
                  )}

                  {step === "map-team" && (createdImportId || createdSourceId) && (
                    <MapTeamStep
                      ref={mapTeamStepRef}
                      dataImportId={createdImportId ?? undefined}
                      dataImportSourceId={createdImportId ? undefined : (createdSourceId ?? undefined)}
                      onNext={isEnterprise ? () => setStep("enterprise-processing") : handleNext}
                      onReadyChange={setMapTeamReady}
                    />
                  )}

                  {step === "review" && createdImportId && (
                    <ReviewStep ref={reviewStepRef} dataImportId={createdImportId} onStatusChange={setReviewStatus} />
                  )}

                  {step === "importing" && createdImportId && (
                    <ImportProgress
                      dataImportId={createdImportId}
                      onComplete={handleImportComplete}
                      onClose={handleClose}
                      autoCloseOnImporting={autoCloseOnImporting}
                      onImportStarted={onImportStarted}
                    />
                  )}

                  {step === "completed" && (
                    <div className="flex flex-1 flex-col items-center justify-center">
                      <ImportCompleteSummary statistics={completionStats} />
                      <Button onClick={handleClose}>Close</Button>
                    </div>
                  )}

                  {step === "enterprise-processing" && (
                    <div className="mx-auto flex max-w-xl flex-col items-center gap-6 py-12 text-center">
                      <CheckCircle2 className="text-green-accent h-16 w-16" />
                      <div>
                        <h3 className="text-xl font-semibold">
                          {isConnectedImportSource(selectedType)
                            ? `Your ${selectedSource?.name ?? "CRM"} import is pending`
                            : "We're importing your data"}
                        </h3>
                        <p className="text-muted-foreground mt-2">
                          {isConnectedImportSource(selectedType)
                            ? `Your ${selectedSource?.name ?? "CRM"} account is connected. The Slant team is preparing your import and will contact you when it's ready.`
                            : "Your file has been uploaded. The Slant team is preparing your import and will contact you when it's ready."}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer - hidden on importing, completed, and enterprise-processing steps which have their own buttons */}
            {!isImportingStep && !isCompletedStep && !isEnterpriseProcessingStep && (
              <div className="border-border flex items-center justify-between border-t px-8 py-4">
                <div>
                  {canGoBack && !isProcessing && (
                    <Button variant="ghost" onClick={handleBack} disabled={createCompanySource.isPending}>
                      <ArrowLeft />
                      Back
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {(!isProcessing || isAwaitingCredentials) && (
                    <>
                      {isAwaitingCredentials ? (
                        <Button
                          onClick={handleCredentialRetry}
                          disabled={credentialsMissing || updateSourceCredentials.isPending}
                        >
                          {updateSourceCredentials.isPending && <Loader2 className="animate-spin" />}
                          {updateSourceCredentials.isPending ? "Retrying..." : "Retry sign-in"}
                        </Button>
                      ) : isCreateStep ? (
                        <Button
                          onClick={handleCreate}
                          disabled={!file || isUploading || credentialsMissing || scopeMissing}
                        >
                          {isUploading && <Loader2 className="animate-spin" />}
                          {isUploading ? "Uploading..." : isEnterprise ? "Upload" : "Create import"}
                        </Button>
                      ) : isLastStep ? (
                        <>
                          {reviewStatus.pendingCount > 0 && (
                            <Button
                              variant="outline"
                              onClick={() => reviewStepRef.current?.saveChanges()}
                              disabled={reviewStatus.isSavePending}
                            >
                              {reviewStatus.isSavePending && <Loader2 className="animate-spin" />}
                              Save
                            </Button>
                          )}
                          <Button
                            onClick={async () => {
                              await reviewStepRef.current?.startImport()
                              setStep("importing")
                            }}
                            disabled={reviewStatus.isStartPending || reviewStatus.includedCount === 0}
                          >
                            {reviewStatus.isStartPending && <Loader2 className="animate-spin" />}
                            Import
                          </Button>
                        </>
                      ) : step === "select-source" ? (
                        <Button onClick={handleNext} disabled={!selectedType}>
                          Next
                          <ArrowRight />
                        </Button>
                      ) : step === "upload-file" ? (
                        <Button onClick={handleNext} disabled={!file || scopeMissing}>
                          Next
                          <ArrowRight />
                        </Button>
                      ) : step === "filter" ? (
                        <Button
                          onClick={async () => {
                            setFilterStepPending(true)
                            try {
                              await filterStepRef.current?.submit()
                            } finally {
                              setFilterStepPending(false)
                            }
                          }}
                          disabled={filterStepPending}
                        >
                          {filterStepPending && <Loader2 className="animate-spin" />}
                          Continue to mappings
                          <ArrowRight />
                        </Button>
                      ) : step === "data-mappings" ? (
                        <Button
                          onClick={async () => {
                            setDataMappingsStepPending(true)
                            try {
                              await dataMappingsStepRef.current?.submit()
                            } finally {
                              setDataMappingsStepPending(false)
                            }
                          }}
                          disabled={dataMappingsStepPending}
                        >
                          {dataMappingsStepPending && <Loader2 className="animate-spin" />}
                          {dataMappingsButtonLabel}
                          <ArrowRight />
                        </Button>
                      ) : step === "map-team" ? (
                        <Button
                          onClick={async () => {
                            setMapTeamStepPending(true)
                            try {
                              await mapTeamStepRef.current?.submit()
                            } finally {
                              setMapTeamStepPending(false)
                            }
                          }}
                          disabled={mapTeamStepPending || !mapTeamReady}
                        >
                          {mapTeamStepPending && <Loader2 className="animate-spin" />}
                          Continue
                          <ArrowRight />
                        </Button>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </CustomDialog>
      <ConfirmDialog
        open={showLeaveConfirm}
        onOpenChange={setShowLeaveConfirm}
        title={"Leave before verification finishes?"}
        description={
          "We're still checking whether Wealthbox needs a verification code. If you leave now, the import may get stuck and you'll need to start it over from scratch."
        }
        dismiss={"explicit"}
        action={{ label: "Leave anyway", onClick: handleConfirmLeave, destructive: true }}
        cancel={{ onClick: () => setShowLeaveConfirm(false) }}
      />
    </>
  )
}
